import {
  type TouchEvent as ReactTouchEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Check, CheckCheck, History, Reply } from "lucide-react";
import { clsx } from "clsx";
import type { ChatMessage, ChatReactionGroup } from "../types";
import { Avatar } from "./Avatar";
import { LinkPreviewCard } from "./LinkPreviewCard";
import { MessageActions } from "./MessageActions";
import { MessageAttachments } from "./MessageAttachments";
import { ReplyQuote } from "./ReplyQuote";

const CLAMP_LINES = 5;
const EDIT_WINDOW_MS = 10 * 60 * 1000;
const LONG_PRESS_MS = 450;
const SWIPE_REPLY_THRESHOLD = 70;
const SWIPE_MAX = 100;

interface MessageBubbleProps {
  message: ChatMessage;
  mine: boolean;
  showSender: boolean;
  /** First bubble of a same-sender run — gets the extra top gap. */
  groupStart: boolean;
  /** Last bubble of a run (or a lone one) — keeps its timestamp row visible;
   *  the rest reveal it on hover, so a burst of messages reads as one block
   *  instead of a column of repeated clock stamps. */
  showTime: boolean;
  highlightTerm: string;
  privileged: boolean;
  onReact: (messageId: string, emoji: string) => void;
  onClearReaction: (messageId: string) => void;
  onReply: (message: ChatMessage) => void;
  onEdit: (messageId: string, body: string) => void;
  onDelete: (messageId: string) => void;
  onShowHistory: (messageId: string) => void;
  onJumpToReply: (id: string) => void;
  onOpenReactions: (message: ChatMessage, emoji?: string) => void;
  registerRef?: (id: string, el: HTMLDivElement | null) => void;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString("pl-PL", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function highlightedBody(
  body: string,
  term: string,
  mentionNames: string[] = [],
): React.ReactNode {
  if (!term && !mentionNames.length) return body;

  type Range = { start: number; end: number; type: "mention" | "term" };
  const ranges: Range[] = [];

  if (mentionNames.length) {
    for (const name of [...mentionNames].sort((a, b) => b.length - a.length)) {
      const re = new RegExp(
        `@${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
        "g",
      );
      let m: RegExpExecArray | null;
      while ((m = re.exec(body)) !== null) {
        ranges.push({ start: m.index, end: m.index + m[0].length, type: "mention" });
      }
    }
  }

  if (term) {
    const lower = body.toLowerCase();
    const t = term.toLowerCase();
    let idx = 0;
    while ((idx = lower.indexOf(t, idx)) !== -1) {
      ranges.push({ start: idx, end: idx + term.length, type: "term" });
      idx += term.length;
    }
  }

  if (!ranges.length) return body;

  ranges.sort((a, b) => a.start - b.start || a.end - b.end);

  // Merge overlapping ranges (first one wins)
  const merged: Range[] = [];
  for (const r of ranges) {
    if (merged.length && r.start < merged[merged.length - 1].end) continue;
    merged.push(r);
  }

  const parts: React.ReactNode[] = [];
  let pos = 0;
  let key = 0;
  for (const { start, end, type } of merged) {
    if (start > pos) parts.push(<span key={key++}>{body.slice(pos, start)}</span>);
    const chunk = body.slice(start, end);
    if (type === "mention") {
      parts.push(
        <span key={key++} className="mention">
          {chunk}
        </span>,
      );
    } else {
      parts.push(
        <mark key={key++} className="bg-amber-200 text-inherit">
          {chunk}
        </mark>,
      );
    }
    pos = end;
  }
  if (pos < body.length) parts.push(<span key={key++}>{body.slice(pos)}</span>);
  return parts;
}

function ReactionBar({
  groups,
  mine,
  onOpen,
}: {
  groups: ChatReactionGroup[];
  mine: boolean;
  onOpen: (emoji: string) => void;
}) {
  if (groups.length === 0) return null;

  const sorted = [...groups].sort((a, b) => b.count - a.count);
  const top4 = sorted.slice(0, 4);
  const totalCount = groups.reduce((sum, g) => sum + g.count, 0);
  const viewerReacted = groups.some((g) => g.by_viewer);

  return (
    <div
      className={clsx(
        "absolute z-10",
        "-bottom-3",
        mine ? "right-2" : "left-2",
      )}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpen(sorted[0]?.emoji ?? "");
        }}
        title="Kto zareagował"
        className={clsx(
          "text-[11px] rounded-full px-2 py-0.5 border flex items-center gap-1 leading-none shadow-sm whitespace-nowrap",
          viewerReacted
            ? "border-red-400 bg-white text-red-800"
            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50",
        )}
      >
        <span className="flex">
          {top4.map((g) => (
            <span key={g.emoji} className="text-[13px] leading-none">{g.emoji}</span>
          ))}
        </span>
        <span className="font-mono">{totalCount}</span>
      </button>
    </div>
  );
}

export function MessageBubble({
  message: m,
  mine,
  showSender,
  groupStart,
  showTime,
  highlightTerm,
  privileged,
  onReact,
  onClearReaction,
  onReply,
  onEdit,
  onDelete,
  onShowHistory,
  onJumpToReply,
  onOpenReactions,
  registerRef,
}: MessageBubbleProps) {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.body);
  const [bubbleHovered, setBubbleHovered] = useState(false);
  const [panelHovered, setPanelHovered] = useState(false);
  const [forceShown, setForceShown] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    registerRef?.(m.id, wrapperRef.current);
    return () => registerRef?.(m.id, null);
  }, [m.id, registerRef]);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || expanded) return;
    setIsClamped(el.scrollHeight - el.clientHeight > 1);
  }, [m.body, expanded, m.id]);

  const canEdit = useMemo(() => {
    if (!mine || m.is_deleted) return false;
    return Date.now() - new Date(m.created_at).getTime() < EDIT_WINDOW_MS;
  }, [mine, m.is_deleted, m.created_at]);

  const isTouch =
    typeof window !== "undefined" &&
    "matchMedia" in window &&
    window.matchMedia("(hover: none)").matches;

  const visible =
    !m.is_deleted && !editing && (bubbleHovered || panelHovered || forceShown);

  // Dismiss forced/touch panel on outside tap.
  useEffect(() => {
    if (!forceShown) return;
    function onTap(e: MouseEvent | TouchEvent) {
      const bubble = bubbleRef.current;
      if (!bubble) return;
      if (!bubble.contains(e.target as Node)) setForceShown(false);
    }
    window.addEventListener("touchstart", onTap, { passive: true });
    window.addEventListener("mousedown", onTap);
    return () => {
      window.removeEventListener("touchstart", onTap);
      window.removeEventListener("mousedown", onTap);
    };
  }, [forceShown]);

  // ── Touch handlers: long-press → action panel; swipe-right → reply ──
  function onTouchStart(e: ReactTouchEvent<HTMLDivElement>) {
    if (!isTouch || editing || m.is_deleted) return;
    const t = e.touches[0];
    swipeStart.current = { x: t.clientX, y: t.clientY };
    longPressTimer.current = setTimeout(() => {
      setForceShown(true);
      // Vibrate a short pulse if supported, for tactile feedback.
      if (typeof navigator !== "undefined" && "vibrate" in navigator) {
        try {
          (navigator as Navigator & { vibrate?: (p: number) => void }).vibrate?.(20);
        } catch {
          /* noop */
        }
      }
    }, LONG_PRESS_MS);
  }

  function onTouchMove(e: ReactTouchEvent<HTMLDivElement>) {
    const start = swipeStart.current;
    if (!start) return;
    const t = e.touches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      // Cancel long-press once user starts moving.
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
    // Only horizontal-right gestures count as swipe-reply.
    if (dx > 0 && Math.abs(dx) > Math.abs(dy)) {
      setSwipeOffset(Math.min(dx, SWIPE_MAX));
    }
  }

  function onTouchEnd() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (swipeOffset >= SWIPE_REPLY_THRESHOLD) {
      onReply(m);
    }
    setSwipeOffset(0);
    swipeStart.current = null;
  }

  const senderName =
    m.sender?.full_name || m.sender?.username || "(nieznany)";

  const viewerReacted = m.reactions.some((r) => r.by_viewer);

  const hovered = bubbleHovered || panelHovered || forceShown;
  // The clock/receipt row sits inside the bubble on the closing message of a run
  // and on anything carrying extra state (edited, deleted). Mid-run bubbles get
  // it in the gutter on hover instead — repeating "17:38" under every line of a
  // burst was the noisiest thing in the thread.
  const metaInline = showTime || !!m.edited_at || m.is_deleted;

  const metaContent = (
    <>
      {m.edited_at && !m.is_deleted && (
        <button
          type="button"
          onClick={privileged ? () => onShowHistory(m.id) : undefined}
          className={clsx(
            "flex items-center gap-0.5 italic",
            privileged && "underline hover:opacity-100",
          )}
          disabled={!privileged}
        >
          edytowane
          {privileged && <History size={10} />}
        </button>
      )}
      {m.is_deleted && privileged && (
        <span className="italic text-red-600">[soft-delete]</span>
      )}
      <span>{formatTime(m.created_at)}</span>
      {mine && !m.is_deleted && m.read_state === "sent" && (
        <Check size={13} className="-ml-0.5 text-gray-400" strokeWidth={2.5} />
      )}
      {mine && !m.is_deleted && m.read_state === "read" && (
        <CheckCheck size={13} className="-ml-0.5 text-blue-600" strokeWidth={2.5} />
      )}
    </>
  );

  return (
    <div
      ref={wrapperRef}
      className={clsx(
        "relative flex gap-2",
        mine ? "justify-end" : "justify-start",
        // Tight inside a run, airy between speakers — the rhythm is what makes
        // a long thread scannable.
        groupStart ? "mt-3 first:mt-0" : "mt-0.5",
        // Extra bottom gap so the reaction pills (overhanging -bottom-3) don't
        // overlap the next bubble's hover action bar (anchored at -top-4).
        m.reactions.length > 0 && "mb-10",
        // The hover bar and the emoji panels hang out of the bubble and have to
        // paint over the messages BELOW them, which come later in the DOM. The
        // row is only lifted while it is actually being used, so a raised row
        // never sits over its neighbours' own panels.
        (hovered || menuOpen) && "z-30",
      )}
      style={
        // NOTE: no identity transform here. `translateX(0)` still makes every
        // row a stacking context, which trapped the emoji panel behind the
        // following messages; only the live swipe gets a transform now.
        swipeOffset > 0
          ? { transform: `translateX(${swipeOffset}px)`, transition: "transform 60ms linear" }
          : { transition: "transform 180ms ease-out" }
      }
    >
      {swipeOffset >= SWIPE_REPLY_THRESHOLD - 10 && (
        <div className="absolute left-2 top-1/2 -translate-y-1/2 text-red-600 flex items-center gap-1 text-xs font-semibold">
          <Reply size={14} /> Odpowiedz
        </div>
      )}
      {!mine && showSender ? (
        <Avatar user={m.sender} size={28} />
      ) : !mine ? (
        <div className="w-7 flex-shrink-0" />
      ) : null}
      <div
        ref={bubbleRef}
        onMouseEnter={() => !isTouch && setBubbleHovered(true)}
        onMouseLeave={() => !isTouch && setBubbleHovered(false)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className={clsx(
          // Own messages used to be solid brand red — a whole thread of them
          // read as a column of error banners and pushed the timestamps to
          // 10px white-on-red. Ownership now rides on the alignment plus a
          // single hard accent edge, so the text keeps full contrast.
          "relative max-w-[85%] sm:max-w-[70%] px-2.5 py-1.5 text-sm leading-snug text-gray-900",
          mine
            ? "chat-bubble-mine border border-red-200 border-r-[3px] border-r-red-600 bg-red-50"
            : "border border-gray-200 border-l-[3px] border-l-gray-300 bg-white",
          m.is_deleted && "opacity-70 italic",
        )}
      >
        <MessageActions
          visible={visible}
          mine={mine}
          canEdit={canEdit}
          viewerReacted={viewerReacted}
          onHoverChange={setPanelHovered}
          onMenuOpenChange={setMenuOpen}
          onReact={(e) => {
            onReact(m.id, e);
            setForceShown(false);
          }}
          onClearReaction={() => {
            onClearReaction(m.id);
            setForceShown(false);
          }}
          onReply={() => {
            onReply(m);
            setForceShown(false);
          }}
          onEdit={() => {
            setDraft(m.body);
            setEditing(true);
            setForceShown(false);
            setBubbleHovered(false);
            setPanelHovered(false);
          }}
          onDelete={() => {
            onDelete(m.id);
            setForceShown(false);
          }}
        />

        {!mine && showSender && (
          <div className="mb-0.5 font-mono text-[10px] font-semibold tracking-wide text-gray-500">
            {senderName}
            {/* Kto pisze z zewnątrz, ma to napisane przy nazwisku — a nie tylko
                na liście składu, do której nikt nie zagląda w trakcie pisania. */}
            {m.sender?.is_external && (
              <span className="ml-1 font-normal text-amber-700">
                · {m.sender.company_name || "podwykonawca"}
              </span>
            )}
          </div>
        )}

        {m.reply_to && (
          <ReplyQuote
            reply={m.reply_to}
            mine={mine}
            onJump={onJumpToReply}
          />
        )}

        {/* Image / file attachments rendered ABOVE the text, as in WhatsApp. */}
        {!m.is_deleted && m.attachments.length > 0 && (
          <MessageAttachments attachments={m.attachments} />
        )}

        {m.is_deleted && !m.body ? (
          <div>[wiadomość usunięta]</div>
        ) : editing ? (
          <div className="flex flex-col gap-1">
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setEditing(false);
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (draft.trim()) {
                    onEdit(m.id, draft);
                    setEditing(false);
                  }
                }
              }}
              rows={3}
              className="border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:border-red-500 focus:outline-none"
            />
            <div className="flex gap-2 text-[11px]">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="text-gray-500 underline"
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!draft.trim()) return;
                  onEdit(m.id, draft);
                  setEditing(false);
                }}
                className="font-semibold text-red-600 underline"
              >
                Zapisz (Enter)
              </button>
            </div>
          </div>
        ) : (
          m.body && (
            <>
              <div
                ref={bodyRef}
                className="whitespace-pre-wrap break-words"
                style={
                  expanded
                    ? undefined
                    : {
                        display: "-webkit-box",
                        WebkitLineClamp: CLAMP_LINES,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }
                }
              >
                {highlightedBody(
                  m.body,
                  highlightTerm,
                  [
                    ...(m.mentions ?? []).map((x) => x.full_name),
                    // "wszyscy"/"all" are not real user names but need
                    // mention highlighting when @wszyscy/@all appears in text.
                    "wszyscy",
                    "all",
                  ],
                )}
              </div>
              {isClamped && (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-0.5 text-[11px] font-medium text-red-600 underline"
                >
                  {expanded ? "Zwiń" : "Pokaż więcej"}
                </button>
              )}
            </>
          )
        )}

        {!m.is_deleted &&
          m.link_previews.map((lp, i) => (
            <LinkPreviewCard key={`${lp.url}-${i}`} preview={lp} mine={mine} />
          ))}

        {metaInline ? (
          <div
            className={clsx(
              "mt-0.5 flex items-center gap-1.5 text-[10px] text-gray-400",
              mine && "justify-end",
            )}
          >
            {metaContent}
          </div>
        ) : (
          // Mid-run bubble: the stamp lives in the gutter next to the bubble, so
          // it costs no height and hovering never reflows the thread.
          <div
            className={clsx(
              "pointer-events-none absolute top-1/2 z-10 flex -translate-y-1/2 items-center gap-1.5 whitespace-nowrap text-[10px] text-gray-400 transition-opacity",
              mine ? "right-full mr-2" : "left-full ml-2",
              hovered ? "opacity-100" : "opacity-0",
            )}
          >
            {metaContent}
          </div>
        )}

        <ReactionBar
          groups={m.reactions}
          mine={mine}
          onOpen={(e) => onOpenReactions(m, e)}
        />
      </div>
    </div>
  );
}
